import {DateTime} from 'luxon'
import {createLogger} from '~/utils/logger'

const logger = createLogger('OrderEvents')

// Global state for order subscriptions - shared across all components
// Memory management: Limit cached orders to prevent unbounded growth
const MAX_CACHED_ORDERS = 100
const MAX_PROCESSED_IDS = 200

const globalOrderState = reactive({
    isSubscribed: false,
    channelName: null,
    businessChannel: null,
    incomingOrders: [],
    processedOrderIds: new Set(), // Track processed IDs to prevent duplicates
})

let audioInstance = null
const initAudio = (soundUrl = '/sounds/alert3.mp3') => {
    if (!audioInstance && process.client) {
        audioInstance = new Audio(soundUrl)
    } else if (audioInstance && audioInstance.src !== soundUrl && process.client) {
        audioInstance.src = soundUrl
    }
    return audioInstance
}

const playNotificationSound = (isOnlineOrder = false) => {
    const userStore = useUserStore()
    console.log('[NOTEIFICATION SOUND]', userStore.notificationSound)
    if (!userStore.notificationSoundEnabled) {
        logger.debug('Notification sound is muted')
        return
    }

    const soundUrl = userStore.notificationSound || '/sounds/alert3.mp3'
    const audio = initAudio(isOnlineOrder ? '/sounds/alert.mp3' : soundUrl)
    if (audio) {
        stopNotificationSound()
        // Online orders always loop until acknowledged (critical)
        // POS notifications respect user setting
        audio.loop = isOnlineOrder ? true : userStore.notificationSoundLoop
        audio.play().catch(e => logger.warn('Failed to play notification sound:', e))
        logger.debug(`Playing alert sound (loop: ${audio.loop}):`, soundUrl)
    }
}

const stopNotificationSound = () => {
    if (audioInstance) {
        audioInstance.pause()
        audioInstance.currentTime = 0
        logger.debug('Stopped alert sound')
    }
}

// Order statuses
export const ORDER_STATUSES = {
    PENDING_BUSINESS_ACCEPTANCE: 'pending_business_acceptance',
    ACCEPTED_PREPARING: 'accepted_preparing',
    PREPARING: 'preparing',
    READY_FOR_PICKUP: 'ready_for_pickup',
    READY: 'ready',
    OUT_FOR_DELIVERY: 'out_for_delivery',
    COMPLETED: 'completed',
    PAID: 'paid',
    DELIVERED: 'delivered',
    REJECTED_BY_BUSINESS: 'rejected_by_business',
    CANCELLED_BY_BUSINESS: 'cancelled_by_business',
    PAYMENT_FAILED: 'payment_failed',
    VOID_PAYMENT_FAILED: 'void_payment_failed',
}

const terminalStatuses = [
    ORDER_STATUSES.COMPLETED,
    ORDER_STATUSES.PAID,
    ORDER_STATUSES.DELIVERED,
    ORDER_STATUSES.REJECTED_BY_BUSINESS,
    ORDER_STATUSES.CANCELLED_BY_BUSINESS,
    ORDER_STATUSES.VOID_PAYMENT_FAILED,
    ORDER_STATUSES.PAYMENT_FAILED,
]

export const useOrderEvents = () => {
    const {$sc: socket} = useNuxtApp()
    const userStore = useUserStore()
    const socketStore = useSocketStore()

    const isTerminalStatus = status => terminalStatuses.includes(status)

    const handleIncomingEvent = async (eventType, payload) => {
        const {saveLocalOrder, getLocalOrder, getInstanceId} = usePosDatabase()
        const {emitOnlineOrderCreated, emitOnlineOrderUpdated, emitOnlineOrderStatusChanged} = usePosEventBus()
        const currentDeviceId = await getInstanceId()
        const {useNotificationStore} = await import('~/stores/notifications')
        const notificationStore = useNotificationStore()

        const orderId = payload.id || payload.orderId
        const displayOrderId = payload.orderNumber || orderId
        let currentStatus = payload.status || 'unknown'

        logger.event(eventType, payload)

        if (eventType === 'new_order') {
            currentStatus = payload.status || ORDER_STATUSES.PENDING_BUSINESS_ACCEPTANCE

            const existingOrder = await getLocalOrder(`online_${orderId}`)

            if (payload.isRetry && existingOrder) {
                logger.info(`Retry event for existing order ${orderId} - skipping save to preserve order details`)
                emitOnlineOrderCreated(existingOrder)
                if (currentDeviceId === 'pos') playNotificationSound(true)
                return
            }

            if (!payload.orderDetails && !payload.items && existingOrder) {
                logger.info(`Event missing order details for ${orderId} but order exists - skipping save`)
                emitOnlineOrderCreated(existingOrder)
                if (currentDeviceId === 'pos') playNotificationSound(true)
                return
            }

            const orderToSave = {
                id: `online_${orderId}`,
                orderNumber: displayOrderId,
                orderType: payload.orderType || 'pickup',
                customerName: payload.customerName || `${payload.customerFirstName || ''} ${payload.customerLastName || ''}`.trim(),
                customerPhone: payload.customerPhone,
                customerEmail: payload.customerEmail,
                deliveryAddressLine1: payload.deliveryAddress?.line1,
                deliveryCity: payload.deliveryAddress?.city,
                deliveryState: payload.deliveryAddress?.state,
                deliveryZipCode: payload.deliveryAddress?.zipCode,
                orderDetails: payload.orderDetails || payload.items || [],
                subtotal: payload.subtotal || 0,
                total: payload.totalAmount || payload.total || 0,
                tax: payload.taxAmount || 0,
                tip: payload.tipAmount || 0,
                discount: payload.discountAmount || payload.discount || 0,
                status: currentStatus,
                isOnlineOrder: true,
                backendOrderId: orderId,
                tableName: payload.tableNumber || payload.tableName,
                createdAt: payload.placedAt || payload.createdAt || new Date().toISOString(),
                customerId: payload.customerId,
                accountId: payload.accountId,
            }

            await saveLocalOrder(orderToSave)

            emitOnlineOrderCreated(orderToSave)

            if (currentDeviceId === 'pos') playNotificationSound(true) // Online orders loop until acknowledged, only on POS

            notificationStore.addNotification({
                type: 'new_order',
                title: 'New Order Received',
                subtitle: `Order #${displayOrderId} - ${orderToSave.customerName} - $${Number(orderToSave.total || 0).toFixed(2)}`,
                orderId: orderToSave.id,
                orderNumber: displayOrderId,
                link: '/orders',
                icon: 'mdi-cart-check',
                color: 'success'
            })

            logger.success('New order saved to local DB:', orderToSave.id)
        } else if (eventType === 'order_payment_completed') {
            // Payment completed from POS - remove order from tablets/KDS
            currentStatus = 'completed'

            // Try both local POS ID and online order ID formats
            const localId = payload.posLocalId
            const onlineId = `online_${orderId}`

            // Check if this is a local POS order or online order
            let existingOrder = null
            if (localId) {
                existingOrder = await getLocalOrder(localId)
            }
            if (!existingOrder) {
                existingOrder = await getLocalOrder(onlineId)
            }

            if (existingOrder) {
                const orderIdToUpdate = existingOrder.id
                await saveLocalOrder({
                    ...existingOrder,
                    status: currentStatus,
                    paymentStatus: 'paid',
                })

                emitOnlineOrderUpdated({...existingOrder, status: currentStatus, paymentStatus: 'paid'})
                emitOnlineOrderStatusChanged(orderIdToUpdate, currentStatus)

                logger.success('Order payment completed, status updated:', orderIdToUpdate)
            }
        } else if (eventType === 'payment:captured') {
            const existingOrder = await getLocalOrder(`online_${orderId}`)
            if (existingOrder) {
                await saveLocalOrder({
                    ...existingOrder,
                    paymentStatus: 'captured',
                    paymentCaptureConfirmed: true,
                    stripeChargeId: payload.stripeChargeId || null,
                })

                emitOnlineOrderUpdated({
                    ...existingOrder,
                    paymentStatus: 'captured',
                    paymentCaptureConfirmed: true,
                })

                const {emitPaymentCaptured} = usePosEventBus()
                emitPaymentCaptured(orderId, payload)

                logger.success('Payment captured for order:', `online_${orderId}`)
            }
        } else if (eventType === 'order_status_update' || eventType === 'payment_capture_failed') {
            currentStatus = payload.status || (eventType === 'payment_capture_failed' ? ORDER_STATUSES.PAYMENT_FAILED : 'unknown')

            const existingOrder = await getLocalOrder(`online_${orderId}`)
            if (existingOrder) {
                await saveLocalOrder({
                    ...existingOrder,
                    status: currentStatus,
                })

                emitOnlineOrderUpdated({...existingOrder, status: currentStatus})
                emitOnlineOrderStatusChanged(`online_${orderId}`, currentStatus)

                // Show critical notification for payment failures
                if (eventType === 'payment_capture_failed' || currentStatus === ORDER_STATUSES.PAYMENT_FAILED) {
                    playNotificationSound(false)
                    notificationStore.addNotification({
                        type: 'payment_failed',
                        title: 'Payment Failed!',
                        subtitle: `Order #${displayOrderId} - Payment could not be captured. DO NOT prepare this order.`,
                        orderId: `online_${orderId}`,
                        orderNumber: displayOrderId,
                        link: '/orders',
                        icon: 'mdi-credit-card-off',
                        color: 'error',
                        persistent: true
                    })
                }

                logger.success('Order status updated in local DB:', `online_${orderId}`, currentStatus)
            }
        }
    }

    const subscribeToOrderEvents = async () => {
        const businessId = userStore.activeBusinessId

        if (!businessId) {
            logger.error('Cannot subscribe to orders: No active business ID')

            return {success: false, error: 'No active business ID'}
        }

        if (globalOrderState.isSubscribed && globalOrderState.channelName === `business-${businessId}-orders`) {
            logger.debug('Already subscribed to order events for this business')

            return {success: true, alreadySubscribed: true}
        }

        if (!socket || !socket.isReady()) {
            logger.error('Cannot subscribe: Socket not ready')

            return {success: false, error: 'Socket not ready'}
        }

        try {
            logger.info('[useOrderEvents] ⚠️ Subscription logic disabled - app.vue now handles all subscriptions (hub-and-spoke)')
            globalOrderState.isSubscribed = true
            globalOrderState.channelName = `business-${businessId}-orders`

            return {success: true}
        } catch (error) {
            logger.error('Failed to subscribe to order events:', error)

            return {success: false, error: error.message}
        }
    }

    const fetchInitialOrders = async () => {
        const businessId = userStore.activeBusinessId

        if (!businessId) {
            logger.error('Cannot fetch orders: No active business ID')

            return
        }

        try {
            logger.info(`Fetching initial active/pending orders for business ${businessId}...`)

            const {data: responseData, error} = await useApi(
                    `/api/orders/active-for-business/${businessId}`,
                    {},
                    {method: 'GET'},
                    1,
            )

            if (error.value) throw error.value

            if (responseData.value && Array.isArray(responseData.value)) {
                const pendingOrdersFromApi = responseData.value

                logger.info(`Found ${pendingOrdersFromApi.length} active orders.`)

                const formattedOrders = pendingOrdersFromApi.map(orderPayload => ({
                    type: 'initial_load',
                    data: orderPayload,
                    timestamp: DateTime.fromISO(orderPayload.placedAt || orderPayload.createdAt),
                    orderId: orderPayload.id,
                    displayOrderId: orderPayload.orderNumber || orderPayload.id,
                    status: orderPayload.status,
                    showRaw: false,
                }))

                const orderIds = new Set(globalOrderState.incomingOrders.map(o => o.orderId))

                formattedOrders.forEach(fo => {
                    if (!orderIds.has(fo.orderId)) {
                        globalOrderState.incomingOrders.push(fo)
                        orderIds.add(fo.orderId)
                    }
                })

                globalOrderState.incomingOrders.sort((a, b) => b.timestamp - a.timestamp)

                // Memory management: Limit cached orders
                if (globalOrderState.incomingOrders.length > MAX_CACHED_ORDERS) {
                    logger.debug(`Trimming cached orders from ${globalOrderState.incomingOrders.length} to ${MAX_CACHED_ORDERS}`)
                    globalOrderState.incomingOrders = globalOrderState.incomingOrders.slice(0, MAX_CACHED_ORDERS)
                }

                // Bound the processed-IDs cache without losing recent state.
                // The previous .clear() was destructive: after eviction, the
                // NEXT event for any already-processed order would re-trigger
                // notification audio + DB writes (customer told twice their
                // order was ready). JS Set preserves insertion order, so we
                // can FIFO-evict the oldest entries while keeping the recent
                // ones — exactly what dedup wants.
                if (globalOrderState.processedOrderIds.size > MAX_PROCESSED_IDS) {
                    const targetSize = Math.floor(MAX_PROCESSED_IDS * 0.75)
                    const toEvict = globalOrderState.processedOrderIds.size - targetSize
                    logger.debug(`Trimming processed order IDs by ${toEvict} (FIFO, keeping newest ${targetSize})`)
                    const iter = globalOrderState.processedOrderIds.values()
                    for (let i = 0; i < toEvict; i++) {
                        const oldest = iter.next().value
                        if (oldest === undefined) break
                        globalOrderState.processedOrderIds.delete(oldest)
                    }
                }
            }
        } catch (err) {
            logger.error('Failed to fetch initial orders:', err)
            throw err
        }
    }

    const unsubscribeFromOrderEvents = () => {
        if (globalOrderState.businessChannel) {
            globalOrderState.businessChannel.unsubscribe()
            globalOrderState.businessChannel = null
        }
        globalOrderState.isSubscribed = false
        globalOrderState.channelName = null
        logger.info('Unsubscribed from order events')
    }

    return {
        isSubscribed: computed(() => globalOrderState.isSubscribed),

        subscribeToOrderEvents,
        unsubscribeFromOrderEvents,
        fetchInitialOrders,
        handleIncomingEvent,
        isTerminalStatus,
        playNotificationSound,
        stopNotificationSound,

        ORDER_STATUSES,
    }
}
